import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { RefreshCw, ArrowLeft, Package } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/orders`);
      setOrders(res.data);
    } catch (e) {
      setError("Could not load orders.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="admin-page">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-12">
        <div className="flex items-center justify-between mb-12">
          <div>
            <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-stone-400 hover:text-amber-400 transition-colors duration-300 mb-6" data-testid="admin-back-link">
              <ArrowLeft className="w-4 h-4" /> Back to site
            </Link>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-stone-50" data-testid="admin-heading">
              Orders
            </h1>
          </div>
          <button
            onClick={load}
            className="rounded-full border border-white/15 px-5 py-2.5 text-sm text-stone-300 hover:border-amber-400/60 hover:text-amber-400 transition-colors duration-300 flex items-center gap-2"
            data-testid="admin-refresh-button"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12">
          <div className="border border-white/10 rounded-2xl p-8 bg-stone-900/40" data-testid="admin-total-orders">
            <Package className="w-5 h-5 text-amber-400 mb-4" />
            <div className="font-serif text-4xl text-stone-50">{orders.length}</div>
            <div className="text-xs uppercase tracking-[0.2em] text-stone-400 mt-2">Total orders</div>
          </div>
          <div className="border border-white/10 rounded-2xl p-8 bg-stone-900/40" data-testid="admin-revenue">
            <span className="font-serif text-2xl text-amber-400 block mb-4">₹</span>
            <div className="font-serif text-4xl text-stone-50">{(orders.length * 1499).toLocaleString("en-IN")}</div>
            <div className="text-xs uppercase tracking-[0.2em] text-stone-400 mt-2">Order value</div>
          </div>
        </div>

        {error && (
          <div className="border border-red-500/30 text-red-300 rounded-xl p-5 mb-8" data-testid="admin-error">{error}</div>
        )}

        <div className="border border-white/10 rounded-2xl overflow-hidden bg-stone-950/50" data-testid="admin-orders-table">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-stone-400">Order ID</TableHead>
                <TableHead className="text-stone-400">Name</TableHead>
                <TableHead className="text-stone-400">Phone</TableHead>
                <TableHead className="text-stone-400">Address</TableHead>
                <TableHead className="text-stone-400">Pack</TableHead>
                <TableHead className="text-stone-400">Placed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.length === 0 && !loading ? (
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableCell colSpan={6} className="text-center text-stone-500 py-16" data-testid="admin-empty-state">
                    No orders yet. They'll appear here the moment someone reserves a pack.
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((o) => (
                  <TableRow key={o.id} className="border-white/10 hover:bg-stone-900/40" data-testid={`admin-order-row-${o.id.slice(0, 8)}`}>
                    <TableCell className="font-mono text-xs text-amber-400/80">{o.id.slice(0, 8)}</TableCell>
                    <TableCell className="text-stone-200">{o.name}</TableCell>
                    <TableCell className="text-stone-300">{o.phone}</TableCell>
                    <TableCell className="text-stone-400 max-w-xs truncate">{o.address}</TableCell>
                    <TableCell className="text-stone-300">{o.pack} · ₹{o.price.toLocaleString("en-IN")}</TableCell>
                    <TableCell className="text-stone-400 text-sm">
                      {new Date(o.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
